import { left } from '@popperjs/core'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/router'
import React from 'react'
import { BarelyVisibleTitle } from '../components/BarelyVisibleTitle'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { CompositionsLayout } from '../components/CompositionsLayout'
import { Content } from '../components/Content'
import { Documents } from '../components/Documents'
import { Filters } from '../components/Filters'
import { Intro } from '../components/Intro'
import { MissingContent } from '../components/MissingContent'
import { Pagination } from '../components/Pagination'
import { ProductList, ProductListKind } from '../components/ProductList'
import { References } from '../components/References'
import { Seo } from '../components/Seo'
import { SingleSelectedColor } from '../components/SingleSelectedColor'
import { Spacer } from '../components/Spacer'
import { StaircaseAssemblyConfiguratorLayout } from '../components/StaircaseAssemblyConfiguratorLayout'
import { StaircaseStepConfiguratorLayout } from '../components/StaircaseStepsConfiguratorLayout'
import { SwimmingPoolConfiguratorLayout } from '../components/SwimmingPoolConfiguratorLayout'
import { TabsPanel } from '../components/TabsPanel'
import { VideoList } from '../components/VideoList'
import { WidthBound } from '../components/WidthBound'
import { useTranslate } from '../src/hooks/useTranslate'
import {
	ProductTypeExtendedFragment,
	ProductFragment,
	SpecialPageType,
	StaircaseAssemblyConfiguratorDataFragment,
	StaircaseStepsConfiguratorDataFragment,
	SwimmingPoolConfiguratorDataFragment,
	ProductTypeFilterSetting,
	ProductCategoryFilterRanges,
	FencePostCapsConfiguratorDataFragment,
} from '../src/model/fragments'
import {
	DEFAULT_LOCALE,
	getLocale,
	getPossibleLocales,
	Locale,
} from '../src/model/locale'
import { extractProductFilterSettingFromQuery, productFilterSettingToQuery } from '../src/model/product'
import { getLinkWithQuery, getProductCategoryLink } from '../src/model/urls'
import { axiosGet } from '../src/utils/axiosWrapper'
import { isDefined } from '../src/utils/isDefined'
import { createPaginator } from '../src/utils/pagination'
import { parseQueryInt } from '../src/utils/query'
import { FencePostCapsConfiguratorLayout } from '../components/FencePostCapsConfiguratorLayout'
import { contentToPlainText } from '../src/model/content'

const PER_PAGE = 15

export default function ProductCategory({
	productCategory,
	possibleLocales,
	locale,
	ranges,
	products,
	filter,
	tabKey,
	referenceId,
	pageInfo
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
	const translate = useTranslate();
	const router = useRouter();

	const data = productCategory ? getLocale(productCategory.locales, possibleLocales) : null
	if (!productCategory || !data) {
		return <MissingContent />
	}

	const documents = productCategory.documents?.items || []
	const references =
		productCategory.references?.items
			.map((x) => x.reference)
			.filter(isDefined) || []
	const compositions = productCategory.compositions

	const initialReferenceIndex =
		tabKey === 'references'
			? references.findIndex((reference) => reference.id === referenceId)
			: undefined

	const productCategoryLink = (
		tabKey: null | string,
		referenceId: null | string = null,
		filters: ProductTypeFilterSetting | undefined = undefined
	) => ({
		...getLinkWithQuery(
			getProductCategoryLink(
				locale,
				productCategory.id,
				productCategory.link?.url ?? '',
			),
			{ 
				tab: tabKey, 
				reference: referenceId,
				...(filters ? {} : {page: pageInfo.currentPage}),
				...(filters ? productFilterSettingToQuery(filters) : {})
			},
		),
		scroll: false,
	})

	const configuratorTab = productCategory.configurator && productCategory.configurator.specialPage !== SpecialPageType.pavementsConfigurator ?  {
		key: null,
		label: 'Konfigurátor',
		wide: false /*productCategory.configurator.specialPage === SpecialPageType.staircaseAssemblyConfigurator*/,
		align: left,
		content: <>
			{
				productCategory.configurator.specialPage === SpecialPageType.staircaseAssemblyConfigurator && 
				<StaircaseAssemblyConfiguratorLayout
					locales={[locale]}
					{...(productCategory.configurator.data as StaircaseAssemblyConfiguratorDataFragment )}
				/>
			}
			{
				productCategory.configurator.specialPage === SpecialPageType.staircaseStepsConfigurator && 
				<StaircaseStepConfiguratorLayout
					locales={[locale]}
					{...(productCategory.configurator.data as StaircaseStepsConfiguratorDataFragment )}
				/>
			}
			{
				productCategory.configurator.specialPage === SpecialPageType.swimmingPoolConfigurator && 
				<SwimmingPoolConfiguratorLayout
					locales={[locale]}
					{...(productCategory.configurator.data as SwimmingPoolConfiguratorDataFragment )}
				/>
			}
			{
				productCategory.configurator.specialPage === SpecialPageType.fencePostCapsConfigurator && 
				<FencePostCapsConfiguratorLayout
					locales={[locale]}
					{...(productCategory.configurator.data as FencePostCapsConfiguratorDataFragment )}
				/>
			}
		</>
	} : null;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const tabs = [
		...(configuratorTab && productCategory.configurator!.first ? [configuratorTab] : []),
		{
			key: null,
			label: 'Produkty',
			content: <>
				<SingleSelectedColor>
					<Filters applicable={productCategory.filters} filter={filter ?? ({} as ProductTypeFilterSetting)} ranges={ranges} onChange={(newFilter) => {
						const link = productCategoryLink(tabKey, null, newFilter);	
						router.push(link.href, link.as, {shallow: false, scroll: false});
					}}  />
				    <ProductList kind={ProductListKind.Generic} items={products.map(p => p.node)} />
					<Spacer initial={40} />
					<Pagination
						currentPage={pageInfo.currentPage}
						pagesCount={pageInfo.lastPage}
					/>
				</SingleSelectedColor>
			</>,
		},
		...(configuratorTab && !productCategory.configurator!.first ? [configuratorTab] : []),
		...(references.length === 0
			? []
			: [
					{
						key: 'references',
						label: translate('references-label'),
						content: (
							<>
								<BarelyVisibleTitle level={2}>
									{translate('references-label')}
								</BarelyVisibleTitle>
								<References
									linkToItem={(itemId: string | null) =>
										productCategoryLink('references', itemId)
									}
									initialIndex={initialReferenceIndex}
									items={references.map((item) => {
										const data = getLocale(item.locales, possibleLocales)

										return {
											id: item.id,
											name: data?.title || '',
											image: item.image,
											listingImage: item.listingImage,
											products: item.products,
											linkProps: productCategoryLink('references', item.id),
										}
									})}
								/>
								<Spacer initial={200} />
							</>
						),
					},
			  ]),
		...(compositions.length === 0
			? []
			: [
					{
						key: 'compositions',
						label: translate('compositions-label'),
						content: (
							<>
								<BarelyVisibleTitle level={2}>
									{translate('compositions-label')}
								</BarelyVisibleTitle>
								<p style={{textAlign: 'center'}}>
									Klikněte na vybranou skladebnost pro zobrazení v ní obsažených produktů.
								</p>
								<Spacer initial={50} />
								<CompositionsLayout 
									compositions={compositions}
								/>
								<Spacer initial={100} />
							</>
						),
					},
			  ]),
		...(documents.length === 0
			? []
			: [
					{
						key: 'documents',
						label: translate('documents-label'),
						content: (
							<>
								<BarelyVisibleTitle level={2}>Ke stažení</BarelyVisibleTitle>
								<Spacer initial={24} />
								<Documents
									items={documents.map((x) => x.document).filter(isDefined)}
								/>
								<Spacer initial={100} />
							</>
						),
					},
			  ]),
		...(data.videos.length === 0
			? []
			: [
					{
						key: 'videos',
						label: translate('videos-label'),
						content: (
							<>
								<BarelyVisibleTitle level={2}>
									{translate('videos-label')}
								</BarelyVisibleTitle>
								<Spacer initial={24} />
								<VideoList items={data.videos} />
								<Spacer initial={100} />
							</>
						),
					},
			  ]),
	]

	// eslint-disable-next-line react-hooks/rules-of-hooks
	const initialTabIndex = React.useMemo(
		() => tabs.findIndex((tab) => tab.key === tabKey) || 0,
		[tabKey, tabs],
	)

	let root = productCategory.parents.length ? productCategory.parents[productCategory.parents.length - 1] : null;
	root = productCategory.id === root?.id ? null : root;
	const customizedSeo = {
		...data.seo,
		titlePostfix: root ? root.name : (data.name.trim() == data.seo.title ? '' : data.name),
		description: data.seo.description ?? contentToPlainText(data.content, 150).split("\n").join(' ').split('  ').join(' '),
		keywords: [translate('fulltext-productCategory'), translate('produktove-typy-detail'), data.name, ...(data.seo.keywords ?? productCategory.parents.map(t => t.name))].filter(k => k),
	}

	return (
		<>
			<Seo seo={customizedSeo} />
			<Breadcrumbs
				items={[
					...(productCategory.parents.map(p => ({
						label: p.name,
						linkProps: getProductCategoryLink(locale, p.id, p.link.url)
					}))).reverse(),
					{
						label: data.name,
					}
				]}
			/>
			<Intro title={data.name} image={productCategory.introImage}>
				<WidthBound>
					<Content content={data.content} innerSize="narrow" viewInsideIntro />
				</WidthBound>
			</Intro>
			<TabsPanel
				initialIndex={initialTabIndex}
				key={productCategory.id}
				items={tabs.map((tab) => ({
					...tab,
					key: undefined,
					linkProps: productCategoryLink(tab.key),
				}))}
			/>
		</>
	)
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => 
{
	const { offset, limit, getPagesInfo } = createPaginator(
		PER_PAGE,
		parseQueryInt(ctx.query.page),
	)

	const queryParams = { ...ctx.query }
	const page = ctx.query.page ?? 1;

	const tabKey =
		typeof queryParams.tab === 'string' && queryParams.tab
			? queryParams.tab
			: null

	const referenceId =
		typeof queryParams.reference === 'string' && queryParams.reference
			? queryParams.reference
			: null

	const filter = extractProductFilterSettingFromQuery(queryParams);
	const locale = (ctx.query.locale || DEFAULT_LOCALE) as Locale
	const possibleLocales = getPossibleLocales(locale);

	const {
		getProductCategory: productCategory,
		ranges: ranges,
		paginateProducts: {
			pageInfo: { totalCount },
			edges,
		}
	} = await axiosGet('/products/get', {
			locale: locale, 
			categoryId: ctx.query.id,
			page: ctx.query.page, 
			limit: limit,
			filter: filter
	}) as
	{
		ranges: ProductCategoryFilterRanges,
		getProductCategory: ProductTypeExtendedFragment,
		paginateProducts: {
			pageInfo: 
			{
				totalCount: number
			},
			edges: {
				node: ProductFragment
			}[]
		}
	};

	return {
		props: {
			page: page,
			pageInfo: getPagesInfo(totalCount),
			locale,
			possibleLocales,
			productCategory,
			ranges,
			products: edges,
			tabKey,
			referenceId,
			filter
		},
	}
}
